import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Edit2, Trash2 } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { getStockById, deleteStock } from '../services/stocks';

export const StockViewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(!location.state?.stock);
  const [error, setError] = useState(null);
  const [stock, setStock] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchStock = async () => {
      try {
        if (location.state?.stock) {
          setStock(location.state.stock);
          setError(null);
          setLoading(false);
          return;
        }
        
        const response = await getStockById(id);
        
        // Handle different response formats
        let stockData = null;
        if (Array.isArray(response) && response.length > 0) {
          stockData = response[0];
        } else if (response && typeof response === 'object' && response.stock) {
          stockData = response.stock;
        } else if (response && typeof response === 'object') {
          stockData = response;
        }
        
        if (!stockData) {
          throw new Error('No stock data found in API response');
        }
        
        setStock(stockData);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load stock');
        setStock(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStock();
  }, [id, location.state]);

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteStock(id);
      navigate('/stocks');
    } catch (err) {
      setError(err.message || 'Failed to delete stock');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading stock...</p>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error || !stock) {
    return (
      <AuthenticatedLayout>
        <div className="space-y-6">
          <button
            onClick={() => navigate('/stocks')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Stocks
          </button>

          <Card>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-red-800">{error || 'Stock not found'}</p>
            </div>
          </Card>
        </div>
      </AuthenticatedLayout>
    );
  }

  const totalValue = (parseInt(stock.quantity) || 0) * (parseFloat(stock.unit_price) || 0);

  return (
    <AuthenticatedLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/stocks')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Stocks
          </button>
        </div>

        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Stock Details</h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">View product information</p>
        </div>

        <Card>
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
              {/* Left column */}
              <div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Product Name</label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{stock.name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Category</label>
                  <p className="text-lg text-gray-900 dark:text-white">{stock.category}</p>
                </div>
              </div>

              {/* Right column */}
              <div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Quantity</label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{parseInt(stock.quantity)} units</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Unit Price</label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">₦{parseFloat(stock.unit_price).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Total Value */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="text-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Total Value</label>
                <p className="text-2xl font-bold text-blue-600">₦{totalValue.toLocaleString()}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <Button
                variant="primary"
                onClick={() => navigate(`/stocks/${id}/edit`, { state: { stock } })}
              >
                <Edit2 className="w-5 h-5 mr-2 inline" />
                Edit Stock
              </Button>
              <Button
                variant="secondary"
                onClick={handleDelete}
                disabled={deleting}
                className="text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-5 h-5 mr-2 inline" />
                {deleting ? 'Deleting...' : 'Delete Stock'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/stocks')}
              >
                Back
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="max-w-md">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Stock</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete this stock item? This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <Button 
                variant="danger" 
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AuthenticatedLayout>
  );
};
